import { useEffect, useRef } from "react";

const EASE = "cubic-bezier(0.32, 0.72, 0, 1)";

/**
 * Swipe-to-dismiss for side drawers / toasts (touch only).
 * `directions`: any of "left" | "right" | "up" — the gestures that dismiss.
 */
export function useSwipeDismiss({ onClose, directions = ["left"], enabled = true, backdropRef }) {
  const ref = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dirKey = directions.join(",");

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return undefined;
    const dirs = dirKey.split(",");
    let sx = 0, sy = 0, st = 0, axis = null, active = false, delta = 0;

    const onStart = (e) => {
      if (e.touches.length !== 1 || e.target.closest?.("input, textarea, select, [data-no-drag]")) return;
      active = true; axis = null; delta = 0;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; st = Date.now();
    };
    const onMove = (e) => {
      if (!active) return;
      const dx = e.touches[0].clientX - sx;
      const dy = e.touches[0].clientY - sy;
      if (!axis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        const dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : (dy < 0 ? "up" : "down");
        if (!dirs.includes(dir)) { active = false; return; }
        axis = dir;
        el.style.animation = "none";
        el.style.transition = "none";
      }
      e.preventDefault();
      if (axis === "right") delta = Math.max(0, dx);
      else if (axis === "left") delta = Math.max(0, -dx);
      else delta = Math.max(0, -dy);
      const t = axis === "right" ? `translateX(${delta}px)` : axis === "left" ? `translateX(${-delta}px)` : `translateY(${-delta}px)`;
      el.style.transform = t;
      el.style.opacity = axis === "up" || el.dataset.swipeFade ? String(Math.max(0.2, 1 - delta / 240)) : "";
      if (backdropRef?.current) backdropRef.current.style.opacity = String(Math.max(0.1, 1 - delta / 400));
    };
    const onEnd = () => {
      if (!active) return;
      active = false;
      if (!axis) return;
      const size = axis === "up" ? el.offsetHeight : el.offsetWidth;
      const v = delta / Math.max(1, Date.now() - st);
      if (delta > Math.min(110, size * 0.35) || v > 0.6) {
        el.style.transition = `transform 200ms ${EASE}, opacity 200ms ease-out`;
        el.style.transform = axis === "right" ? "translateX(110%)" : axis === "left" ? "translateX(-110%)" : "translateY(-140%)";
        el.style.opacity = "0";
        if (backdropRef?.current) { backdropRef.current.style.transition = "opacity 200ms"; backdropRef.current.style.opacity = "0"; }
        setTimeout(() => onCloseRef.current?.(), 190);
      } else {
        el.style.transition = `transform 240ms ${EASE}, opacity 200ms`;
        el.style.transform = "";
        el.style.opacity = "";
        if (backdropRef?.current) { backdropRef.current.style.transition = "opacity 200ms"; backdropRef.current.style.opacity = ""; }
      }
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled, dirKey, backdropRef]);

  return ref;
}
